/**
 * JSON-LD builders.
 *
 * Everything here returns a plain object destined for the `@graph` that
 * `Seo.svelte` emits. One graph per page, with entities referring to each other
 * by `@id`, rather than a separate `<script>` per type: it lets a product point at
 * the publisher instead of restating it, and it is what search engines and AI
 * crawlers actually traverse.
 *
 * Two deliberate omissions, both because Google retired the feature:
 *
 * - **No `HowTo`.** How-to rich results were removed in September 2023. The
 *   cotton-pad germination guide is a textbook `HowTo` and still must not use it.
 * - **No new `FAQPage` for a rich result.** FAQ rich results were retired for
 *   every site on 7 May 2026, so no `FAQPage` here buys a SERP feature and none
 *   should be added expecting one. The markup is still emitted where a page
 *   genuinely carries questions and answers — `/faq`, and the per-strain block
 *   built by `faq()` below — because assistants and AI crawlers do read it, and
 *   a `Question`/`acceptedAnswer` pair is a cleaner retrieval unit than the same
 *   sentence buried in prose. What changed is the expectation, not the markup.
 *
 * `SearchAction` on `WebSite` is kept for machine readability only; the sitelinks
 * search box it once fed no longer exists either.
 *
 * ## The rule every builder here follows
 *
 * **Never emit a fact the site cannot back up.** Not a rating with no reviews
 * behind it, not a `priceValidUntil` picked because a validator asked for one,
 * not an address for premises that no longer exist. Structured data is read by
 * machines that cannot tell a placeholder from a claim, and increasingly it is
 * read by assistants that will repeat it to a customer as fact. Every field
 * below either has a source in this repository or is absent, and the comments
 * say which.
 */

import { SITE_URL, SITE_NAME, absolute } from './config';
/*
 * `HREFLANG` from `$lib/i18n`, its single declaration point, and *not* from
 * `$lib/seo/alternates`, which re-exports the same table. That re-export looks
 * free and is not: `alternates` imports the WooCommerce snapshot, every blog
 * post and every legal page in order to compute the pairings, and this module
 * is imported by `+layout.svelte` — so one convenience import put ~900 KB of
 * data into the client bundle of every page for one lookup table.
 */
import { HREFLANG } from '$lib/i18n';
import { SOCIAL_LINKS } from '$lib/data/social';
import { RESELLERS } from '$lib/data/resellers';
import { reviewsFor, averageRating, RATING_SCALE } from '$lib/data/reviews';
import { parseWeeks } from '$lib/services/strain-facts';
import type { SiteLocale } from '$lib/services/products';

/** Stable `@id`s, so entities can reference each other across the graph. */
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const SHIPPING_ID = `${SITE_URL}/#shipping`;
export const SHIPPING_BANGKOK_ID = `${SITE_URL}/#shipping-bangkok`;
export const RETURN_POLICY_ID = `${SITE_URL}/#returns`;

/** BCP 47 tag for a locale. Reuses the hreflang table so there is one source. */
export const inLanguage = (locale: SiteLocale): string => HREFLANG[locale] ?? locale;

/**
 * The publisher.
 *
 * No `address`, `telephone` or `openingHoursSpecification`, and **not** a
 * `LocalBusiness`. The Bangkok shop has closed — see the note in
 * `pages/contact/Contact.en.svelte` — so this is a distributor with no premises
 * the public can visit. Claiming otherwise in structured data would be a
 * fabricated local-business signal, and the addresses that *are* real belong to
 * the resellers (see `storeList`).
 *
 * The hours below are therefore on the `contactPoint`, not on the organisation:
 * they are when somebody answers, not when a door is open. That distinction is
 * the whole reason the old shop's hours are still being quoted back by
 * assistants from third-party listings.
 *
 * `parentOrganization` carries no `sameAs`. Dutch Passion has no Wikipedia or
 * Wikidata entry to point at — both were checked — and inventing an
 * authoritative identifier is worse than having none, because `sameAs` is
 * specifically the field entity resolvers trust.
 */
export function organization(): Record<string, unknown> {
	return {
		'@type': 'Organization',
		'@id': ORGANIZATION_ID,
		name: SITE_NAME,
		url: SITE_URL,
		/*
		 * A real wordmark, not `/favicon.png`.
		 *
		 * The favicon is a 96px square: below the size Google will use for an
		 * organisation logo, and cropped to the bird rather than the mark. The
		 * file named here is a copy of `assets/dutchpassion-th-logo.png` placed in
		 * `static/` on purpose — assets imported through Vite are rewritten to
		 * hashed filenames at build time and have no URL anything outside a
		 * component can reference, which is the same reason
		 * `sync-product-images.mjs` exists.
		 */
		logo: {
			'@type': 'ImageObject',
			url: absolute('/media/dutchpassion-thailand-logo.png'),
			width: 778,
			height: 417
		},
		description:
			'Distributor of Dutch Passion cannabis seeds in Thailand. Dutch Passion is ' +
			'one of the world’s oldest cannabis seed banks, founded in Amsterdam in 1987.',
		// The homepage's own line, not a new one written for a machine.
		slogan: 'Cannabis seeds, since 1987',
		email: 'info@dutchpassion.co.th',
		// 2023, from the milestone on `/about-us`. The parent's 1987 is separate,
		// below — conflating the two would date this distributor to a founding it
		// had no part in.
		foundingDate: '2023',
		sameAs: SOCIAL_LINKS.map((link) => link.href),
		areaServed: { '@type': 'Country', name: 'Thailand' },
		/*
		 * What this business is an authority on.
		 *
		 * Not keyword stuffing: `knowsAbout` is how an entity graph decides which
		 * questions a source is relevant to, and the honest list here is narrow —
		 * seeds and the growing of them, not cannabis law, medicine or retail.
		 */
		knowsAbout: [
			'Cannabis seeds',
			'Cannabis genetics and breeding',
			'Feminised cannabis seeds',
			'Autoflowering cannabis seeds',
			'CBD-rich cannabis varieties',
			'Cannabis cultivation in tropical climates'
		],
		contactPoint: {
			'@type': 'ContactPoint',
			contactType: 'customer service',
			email: 'info@dutchpassion.co.th',
			areaServed: 'TH',
			availableLanguage: ['English', 'Thai'],
			hoursAvailable: {
				'@type': 'OpeningHoursSpecification',
				dayOfWeek: [
					'https://schema.org/Monday',
					'https://schema.org/Tuesday',
					'https://schema.org/Wednesday',
					'https://schema.org/Thursday',
					'https://schema.org/Friday',
					'https://schema.org/Saturday',
					'https://schema.org/Sunday'
				],
				opens: '10:00',
				closes: '19:00'
			}
		},
		parentOrganization: {
			'@type': 'Organization',
			name: 'Dutch Passion',
			url: 'https://dutch-passion.com',
			foundingDate: '1987',
			// The site's own claim, repeated verbatim rather than enumerated. The
			// individual cups are not recorded anywhere in this repository, and a
			// list assembled from memory would be exactly the kind of invention the
			// module comment forbids.
			award: 'More than 90 cannabis cups'
		}
	};
}

export function website(locale: SiteLocale): Record<string, unknown> {
	return {
		'@type': 'WebSite',
		'@id': WEBSITE_ID,
		url: SITE_URL,
		name: SITE_NAME,
		inLanguage: inLanguage(locale),
		publisher: { '@id': ORGANIZATION_ID },
		potentialAction: {
			'@type': 'SearchAction',
			target: {
				'@type': 'EntryPoint',
				urlTemplate: `${SITE_URL}/shop?q={search_term_string}`
			},
			'query-input': 'required name=search_term_string'
		}
	};
}

/**
 * The page itself, as an entity, carrying when it was last revised.
 *
 * Added because the evergreen pages — FAQ, stockists, the legal-status page —
 * had no freshness signal of any kind, while every competing source answering
 * the same questions carries a current date. `dateModified` comes from
 * `services/content-dates`, which reads `git log`; when it cannot resolve one
 * the field is simply absent. See that module for why a build-time clock read
 * would be worse than nothing.
 *
 * Deliberately separate from `breadcrumbs`: a page can want a trail and no date,
 * or a date and no trail, and folding them together made the callers pass
 * placeholder crumbs.
 */
export function webPage(options: {
	name: string;
	path: string;
	description?: string;
	dateModified?: string;
	datePublished?: string;
	locale: SiteLocale;
}): Record<string, unknown> {
	return {
		'@type': 'WebPage',
		'@id': `${absolute(options.path)}#webpage`,
		url: absolute(options.path),
		name: options.name,
		...(options.description ? { description: options.description } : {}),
		...(options.datePublished ? { datePublished: options.datePublished } : {}),
		...(options.dateModified ? { dateModified: options.dateModified } : {}),
		inLanguage: inLanguage(options.locale),
		isPartOf: { '@id': WEBSITE_ID },
		publisher: { '@id': ORGANIZATION_ID }
	};
}

/**
 * Questions and answers already on the page.
 *
 * Two rules, and both are what separates this from FAQ markup that gets a site
 * penalised. **Every question here is visible in the rendered page** — the
 * callers pass the same array they render, never a second list written for
 * crawlers. And **the answer text is plain text**: `acceptedAnswer` is published
 * verbatim, so HTML in it is published as literal tags.
 *
 * `@id` is per page rather than a shared constant: `/faq` and each of the 22
 * strain pages carry a different set of questions, and one `@id` across all of
 * them would claim they are the same node.
 */
export function faq(props: {
	items: { q: string; a: string }[];
	path: string;
}): Record<string, unknown> {
	const { items, path } = props;

	return {
		'@type': 'FAQPage',
		'@id': `${absolute(path)}#faq`,
		// The strain page's `WebPage` node is not built here, so the FAQ points at
		// the publisher and the page URL rather than dangling.
		url: absolute(path),
		mainEntity: items.map((item) => ({
			'@type': 'Question',
			name: item.q,
			acceptedAnswer: { '@type': 'Answer', text: item.a }
		}))
	};
}

export interface Crumb {
	name: string;
	/** Site-relative path. Omit on the final crumb — the current page. */
	path?: string;
}

/**
 * Breadcrumb trail.
 *
 * Paths must already be resolved for the locale by the caller. Building one here
 * by prefixing `/th` would be wrong: WPML translated page, category and four
 * product slugs, so `/th/<english-path>` generally does not exist. That mistake
 * is the most repeated bug in this codebase.
 */
export function breadcrumbs(trail: Crumb[]): Record<string, unknown> {
	return {
		'@type': 'BreadcrumbList',
		itemListElement: trail.map((crumb, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: crumb.name,
			...(crumb.path ? { item: absolute(crumb.path) } : {})
		}))
	};
}

/**
 * How orders are delivered: a nationwide service, plus one local upgrade.
 *
 * These are two *options a shopper chooses between*, not two regions carved up
 * between them. Getting that backwards misprices the listing:
 *
 * - **Postal, 100 THB — all of Thailand, Bangkok included.** 1–3 business days
 *   in transit after same-day dispatch. This is the baseline every order can
 *   take, and `shippingDestination` is the whole country precisely because
 *   Bangkok is not carved out of it.
 * - **Same-day, 250 THB — Bangkok only.** An additional, faster, dearer option
 *   offered on top of the postal one, not a replacement for it. `DefinedRegion`
 *   scopes it to the city because it is the only place it can be bought.
 *
 * So a Bangkok shopper has both and picks; everyone else has the first. Google
 * reads multiple `shippingDetails` on an offer as alternatives and quotes the
 * cheapest that matches the buyer, which is the behaviour wanted here.
 *
 * Shipping is Thailand-only, which is why `shippingDestination` names one
 * country rather than being omitted.
 *
 * **Both rates are flat, so they can be stated.** Neither varies by basket or
 * weight, so `shippingRate` is a plain `MonetaryAmount` and needs no
 * `shippingRateSettings` table. Google treats a missing rate as an incomplete
 * Merchant listing, which is why these are here rather than left for checkout
 * to reveal: the price a shopper is quoted in a listing has to be one the shop
 * will honour, and a flat rate always is.
 *
 * **These rates must keep matching Article 18.** The terms once described
 * same-day Bangkok delivery as what an order simply gets, rather than as the
 * 250 THB option it is; they now state both rates explicitly, in all eight
 * locales. Structured data that promises more than the contract is the wrong
 * way round, so if a rate or a delivery window changes, `terms.*.md` and this
 * function move together.
 */
export function shippingDetails(): Record<string, unknown>[] {
	return [
		{
			'@type': 'OfferShippingDetails',
			'@id': SHIPPING_ID,
			shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'TH' },
			shippingRate: {
				'@type': 'MonetaryAmount',
				value: 100,
				currency: 'THB'
			},
			deliveryTime: {
				'@type': 'ShippingDeliveryTime',
				handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
				transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' }
			}
		},
		{
			'@type': 'OfferShippingDetails',
			'@id': SHIPPING_BANGKOK_ID,
			shippingDestination: {
				'@type': 'DefinedRegion',
				addressCountry: 'TH',
				// ISO 3166-2 for Bangkok. The readable name goes alongside it rather
				// than instead of it: the code is what a consumer can match on, the
				// name is what one that cannot will still understand.
				addressRegion: 'TH-10',
				name: 'Bangkok'
			},
			shippingRate: {
				'@type': 'MonetaryAmount',
				value: 250,
				currency: 'THB'
			},
			deliveryTime: {
				'@type': 'ShippingDeliveryTime',
				handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
				transitTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' }
			}
		}
	];
}

/**
 * The returns position, as one node the offers reference by `@id`.
 *
 * `MerchantReturnNotPermitted` is not a default and not an omission — it is
 * Article 20 of the terms, in full: "No return for any products purchased."
 * Stating it plainly is better for everyone than leaving it unsaid: an assistant
 * asked "can I return them?" otherwise answers from a competitor's policy, or
 * guesses the consumer default and is wrong.
 */
export function returnPolicy(): Record<string, unknown> {
	return {
		'@type': 'MerchantReturnPolicy',
		'@id': RETURN_POLICY_ID,
		applicableCountry: 'TH',
		returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted'
	};
}

interface ProductLike {
	id: string;
	title: string;
	href: string;
	crossing?: string;
	heroImage?: string;
	urlSlug: string;
	priceRange: { min: number; max: number };
	prices: { size: number; price: number; purchasable?: boolean }[];
	purchasable: boolean;
	attributes?: { key: string; value: string | number | boolean }[];
}

/**
 * Strain attributes, as machine-readable property names.
 *
 * The values live in the strain frontmatter under terse internal keys —
 * `yieldSize`, `thc`, `harvestWeeks` — and on the page they render as an icon
 * beside a bare value, so a crawler reading the text found "XXL · Hybrid · Very
 * High · 10 weeks" with nothing saying what any of it measures. `additionalProperty`
 * is where that gets said.
 *
 * **English names in every locale**, deliberately. `additionalProperty` is read
 * by machines joining facts across sources, not by the reader — the reader gets
 * the localised label on the chip itself — and a property called "ทีเอชซี" on
 * Thai pages and "THC" on English ones is two properties as far as any consumer
 * is concerned.
 */
const PROPERTY_NAMES: Record<string, string> = {
	type: 'Variety type',
	thc: 'THC level',
	cbd: 'CBD level',
	yieldSize: 'Yield',
	harvestWeeks: 'Flowering time',
	family: 'Strain family',
	origin: 'Origin'
};

/** Frontmatter enum values, spelled the way a reader would say them. */
const PROPERTY_VALUES: Record<string, string> = {
	low: 'Low',
	high: 'High',
	very_high: 'Very high',
	extremely_high: 'Extremely high',
	indica: 'Indica',
	sativa: 'Sativa',
	hybrid: 'Hybrid'
};

function additionalProperties(
	attributes: ProductLike['attributes']
): Record<string, unknown>[] | undefined {
	if (!attributes?.length) return undefined;

	const properties = attributes
		.filter((attribute) => PROPERTY_NAMES[attribute.key] && attribute.value !== '')
		.map((attribute) => {
			const raw = String(attribute.value);

			/*
			 * Flowering time is a quantity, and should be usable as one — "which
			 * strain finishes fastest" is a comparison, not a string match.
			 *
			 * Half the catalogue states a range (`"10-11"`, `"12-15"`), so this
			 * emits `minValue`/`maxValue` and only collapses to a single `value`
			 * when the source really is one number. `parseFloat("10-11")` returns
			 * 10, which is how a range quietly becomes a precise claim the breeder
			 * never made.
			 */
			if (attribute.key === 'harvestWeeks') {
				const weeks = parseWeeks(raw);
				if (weeks) {
					return {
						'@type': 'PropertyValue',
						name: PROPERTY_NAMES[attribute.key],
						value: {
							'@type': 'QuantitativeValue',
							...(weeks.min === weeks.max
								? { value: weeks.min }
								: { minValue: weeks.min, maxValue: weeks.max }),
							unitText: 'weeks'
						}
					};
				}
			}

			return {
				'@type': 'PropertyValue',
				name: PROPERTY_NAMES[attribute.key],
				value: PROPERTY_VALUES[raw] ?? raw
			};
		});

	return properties.length > 0 ? properties : undefined;
}

/**
 * A strain.
 *
 * `offers` is **one `Offer` per pack size**, not a single `AggregateOffer`
 * spanning them. The aggregate was true and useless: Auto Critical Orange Punch
 * sells as 1, 3, 7 and 100 seeds, so its range read "฿360 – ฿15,670", and asked
 * what the strain costs an assistant quoted exactly that. A reader wants the
 * price of a pack, and every pack's price is known — it is on the page already.
 *
 * Each offer also carries a `UnitPriceSpecification` giving the price *per seed*,
 * which is the number the packs exist to differentiate and the one question the
 * catalogue could not previously answer in machine-readable form: "which of
 * these is cheapest per seed".
 *
 * Prices come from the real WooCommerce packs, never the `.svx` frontmatter list
 * — that one is stale and omits the 7- and 100-seed packs, and a rich result
 * must not advertise a price checkout will not honour.
 *
 * No `priceValidUntil`. Google's validator asks for one, and there is no date
 * behind it: these prices have no scheduled expiry, so any value would be
 * invented to satisfy a checker. See the rule in the module comment.
 *
 * `image` matters more than it looks: Google will not show a product rich result
 * without one. It comes from `scripts/sync-product-images.mjs`, which exists
 * because the carousel's `enhanced:img` assets have no stable URL.
 */
export function product(
	item: ProductLike,
	locale: SiteLocale,
	description?: string
): Record<string, unknown> {
	const url = absolute(item.href);
	const reviews = reviewsFor(item.id);
	const rating = averageRating(reviews);

	const availability = (purchasable: boolean) =>
		purchasable ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

	const offers = item.prices.map((pack) => ({
		'@type': 'Offer',
		'@id': `${url}#offer-${pack.size}`,
		// Named in seeds because that is the unit the shop sells in and the unit
		// the price is per. "1 seed" is a real product here, not a rounding.
		name: `${pack.size} ${pack.size === 1 ? 'seed' : 'seeds'}`,
		price: pack.price,
		priceCurrency: 'THB',
		availability: availability(pack.purchasable ?? item.purchasable),
		url,
		itemCondition: 'https://schema.org/NewCondition',
		eligibleQuantity: {
			'@type': 'QuantitativeValue',
			value: pack.size,
			unitText: 'seeds'
		},
		priceSpecification: {
			'@type': 'UnitPriceSpecification',
			price: Math.round((pack.price / pack.size) * 100) / 100,
			priceCurrency: 'THB',
			referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'seed' }
		},
		seller: { '@id': ORGANIZATION_ID },
		shippingDetails: [{ '@id': SHIPPING_ID }, { '@id': SHIPPING_BANGKOK_ID }],
		hasMerchantReturnPolicy: { '@id': RETURN_POLICY_ID }
	}));

	return {
		'@type': 'Product',
		'@id': `${url}#product`,
		name: item.title,
		url,
		/*
		 * The page's own description, not `crossing`.
		 *
		 * `crossing` is the genetics line — "Original Orange Bud", "Skunk #1 ×
		 * Afghani" — and it was standing in as the whole description because it
		 * was the only prose field the catalogue held. It reads as a stub next to
		 * a 1,900-word page, and an assistant quoting the `Product` node had three
		 * words to work with.
		 *
		 * `description` is the string the page already publishes as its meta
		 * description, passed in by the route rather than rebuilt here: schema.ts
		 * takes data and returns a graph, and reaching into Paraglide for a
		 * localised sentence would make it a second place product copy is written.
		 * Falling back to `crossing` keeps a caller that passes nothing honest.
		 */
		...(description || item.crossing ? { description: description || item.crossing } : {}),
		...(item.heroImage ? { image: absolute(item.heroImage) } : {}),
		sku: item.urlSlug,
		inLanguage: inLanguage(locale),
		itemCondition: 'https://schema.org/NewCondition',
		brand: { '@type': 'Brand', name: 'Dutch Passion' },
		category: 'Cannabis seeds',
		...(additionalProperties(item.attributes)
			? { additionalProperty: additionalProperties(item.attributes) }
			: {}),
		/*
		 * Ratings and reviews appear only when real ones exist — `reviewsFor`
		 * returns an empty list until somebody publishes one in
		 * `$lib/data/reviews`. A rating is the single most quoted field in this
		 * whole object, so it is the one that must never be manufactured.
		 */
		...(rating !== undefined
			? {
					aggregateRating: {
						'@type': 'AggregateRating',
						ratingValue: rating,
						reviewCount: reviews.length,
						bestRating: RATING_SCALE.best,
						worstRating: RATING_SCALE.worst
					},
					review: reviews.map((entry) => ({
						'@type': 'Review',
						author: { '@type': 'Person', name: entry.author },
						datePublished: entry.datePublished,
						reviewBody: entry.body,
						inLanguage: entry.inLanguage,
						reviewRating: {
							'@type': 'Rating',
							ratingValue: entry.rating,
							bestRating: RATING_SCALE.best,
							worstRating: RATING_SCALE.worst
						}
					}))
				}
			: {}),
		offers
	};
}

/**
 * A category or the shop archive, as a listing.
 *
 * Items are `@id` references to the product pages rather than inlined `Product`
 * objects. Restating 22 full products on every category page would balloon the
 * markup and invite the two copies to disagree.
 */
export function collectionPage(
	options: { name: string; path: string; description?: string; dateModified?: string },
	items: { href: string }[],
	locale: SiteLocale
): Record<string, unknown> {
	return {
		'@type': 'CollectionPage',
		'@id': `${absolute(options.path)}#collection`,
		name: options.name,
		url: absolute(options.path),
		...(options.description ? { description: options.description } : {}),
		...(options.dateModified ? { dateModified: options.dateModified } : {}),
		inLanguage: inLanguage(locale),
		isPartOf: { '@id': WEBSITE_ID },
		mainEntity: {
			'@type': 'ItemList',
			numberOfItems: items.length,
			itemListElement: items.map((item, index) => ({
				'@type': 'ListItem',
				position: index + 1,
				url: absolute(item.href)
			}))
		}
	};
}

interface PostLike {
	title: string;
	path: string;
	date?: string;
	modified?: string;
	excerpt?: string;
	image?: string;
	imageAlt?: string;
	tags?: string[];
}

/**
 * A blog post.
 *
 * `author` is the organisation rather than a person: the posts carry no bylines,
 * and inventing one would be a fabricated E-E-A-T signal. If real authors are
 * added later this should become a `Person` with `worksFor`.
 */
export function blogPosting(post: PostLike, locale: SiteLocale): Record<string, unknown> {
	return {
		'@type': 'BlogPosting',
		'@id': `${absolute(post.path)}#article`,
		headline: post.title,
		url: absolute(post.path),
		...(post.excerpt ? { description: post.excerpt } : {}),
		...(post.date ? { datePublished: post.date } : {}),
		...(post.modified ? { dateModified: post.modified } : {}),
		...(post.image
			? {
					// An `ImageObject` rather than a bare URL, so the photograph carries
					// its own description. The archive thumbnail and the in-body hero are
					// the same file, and until August 2026 both shipped with an empty
					// `alt` — this is the machine-readable half of fixing that.
					image: post.imageAlt
						? {
								'@type': 'ImageObject',
								url: absolute(post.image),
								caption: post.imageAlt
							}
						: absolute(post.image)
				}
			: {}),
		// Topic tags, as the canonical untranslated slugs from `$lib/blog-tags`.
		// Slugs rather than the translated chip labels: `keywords` is read by
		// machines building a topic graph, and a stable English term joins an
		// article to its six translations instead of splitting them across six
		// vocabularies. The reader sees the translated label on the chip.
		...(post.tags?.length ? { keywords: post.tags.join(', ') } : {}),
		inLanguage: inLanguage(locale),
		isPartOf: { '@id': WEBSITE_ID },
		author: { '@id': ORGANIZATION_ID },
		publisher: { '@id': ORGANIZATION_ID }
	};
}

/**
 * Stockists, as `Store` entities.
 *
 * These are the only real addresses connected to this business, and they are
 * third-party shops — so they are marked up as the independent stores they are,
 * not as branches of Dutch Passion Thailand.
 */
export function storeList(path: string): Record<string, unknown> {
	const stores = RESELLERS.flatMap((city) =>
		city.stores.map((store) => ({
			'@type': 'Store',
			name: store.name,
			/*
			 * `addressLocality` belongs *inside* the `PostalAddress`. It sat beside
			 * it until August 2026, where it is not a property of `Store` at all —
			 * so every stockist resolved to nothing more specific than "Thailand"
			 * and the city was dropped on the floor.
			 *
			 * The city also appears inside `streetAddress`, because that is how the
			 * addresses are written in `$lib/data/resellers`. That repetition is
			 * fine: `streetAddress` is the line a human would copy, `addressLocality`
			 * is the field a parser reads.
			 */
			address: {
				'@type': 'PostalAddress',
				streetAddress: store.address,
				addressLocality: city.city,
				addressCountry: 'TH'
			},
			image: absolute(store.logo),
			hasMap: store.mapUrl
		}))
	);

	return {
		'@type': 'ItemList',
		'@id': `${absolute(path)}#stores`,
		name: 'Dutch Passion stockists in Thailand',
		numberOfItems: stores.length,
		itemListElement: stores.map((store, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			item: store
		}))
	};
}
